<script setup lang="ts">
import { computed, ref } from 'vue';
import { IconAdd, IconDelete } from '@/components/design/icons';
import type { ReviewProjectAutoTagTaskGroupInDb } from '@/api';
import { ReviewProjectService } from '@/services/reviewProjectService';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps<{
  show: boolean;
  projectUid: string;
  selectedFileUids: string[];
}>();

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'created', group: ReviewProjectAutoTagTaskGroupInDb): void;
}>();

const message = useGaiaToast();

// A small palette so each new class gets a distinct default color.
const PALETTE = ['#2080f0', '#18a058', '#f0a020', '#d03050', '#8a2be2', '#0891b2'];

interface ClassDraft {
  name: string;
  definition: string;
  color: string;
}

function makeClass(index: number): ClassDraft {
  return { name: '', definition: '', color: PALETTE[index % PALETTE.length] };
}

const classes = ref<ClassDraft[]>([makeClass(0)]);
const multipleChoices = ref<boolean>(false);
const mandatory = ref<boolean>(false);
const submitting = ref<boolean>(false);

const swatchStyle = {
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  border: '1px solid rgba(128, 128, 128, 0.35)',
  cursor: 'pointer',
} as const;

const canSubmit = computed(
  () =>
    classes.value.length > 0 &&
    classes.value.every(c => c.name.trim().length > 0 && c.definition.trim().length > 0)
);

function addClass(): void {
  classes.value.push(makeClass(classes.value.length));
}

function removeClass(index: number): void {
  classes.value.splice(index, 1);
}

function close(): void {
  emit('update:show', false);
}

async function submit(): Promise<void> {
  if (!canSubmit.value) return;
  submitting.value = true;
  try {
    const group = await ReviewProjectService.createAutoTagTaskGroup(props.projectUid, {
      multiple_choices: multipleChoices.value,
      mandatory: mandatory.value,
      options: classes.value.map(c => ({
        name: c.name.trim(),
        definition: c.definition.trim(),
        color: c.color,
      })),
      file_selection: props.selectedFileUids.length
        ? { selection_type: 'selected', file_uids: props.selectedFileUids }
        : { selection_type: 'all_usable' },
    });
    message.success('Auto-tagging started');
    emit('created', group);
    // Reset for the next open.
    classes.value = [makeClass(0)];
    multipleChoices.value = false;
    mandatory.value = false;
    close();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to start auto-tagging.');
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    title="Auto-tag files"
    style="width: 640px"
    :mask-closable="false"
    @update:show="emit('update:show', $event)"
  >
    <n-space vertical :size="16">
      <n-text depth="3">
        {{
          selectedFileUids.length
            ? `Tagging ${selectedFileUids.length} selected file${selectedFileUids.length > 1 ? 's' : ''}.`
            : 'Tagging all usable files in the data room.'
        }}
      </n-text>

      <n-space vertical :size="8">
        <n-flex
          v-for="(item, index) in classes"
          :key="index"
          align="flex-start"
          :size="8"
          :wrap="false"
          style="width: 100%"
        >
          <n-color-picker
            v-model:value="item.color"
            :show-alpha="false"
            :modes="['hex']"
            style="width: 22px"
          >
            <template #trigger="{ value, onClick, ref: triggerRef }">
              <div
                :ref="triggerRef"
                :style="{ ...swatchStyle, backgroundColor: value || '#dbeafe', marginTop: '4px' }"
                @click="onClick"
              />
            </template>
          </n-color-picker>
          <gaia-input v-model:value="item.name" placeholder="Class name *" style="width: 180px" />
          <gaia-textarea
            v-model:value="item.definition"
            placeholder="Definition — when does this class apply?"
            :autosize="{ minRows: 1, maxRows: 3 }"
            style="flex: 1; min-width: 120px"
          />
          <n-button
            quaternary
            circle
            :disabled="classes.length === 1"
            style="margin-top: 2px"
            @click="removeClass(index)"
          >
            <template #icon>
              <n-icon><IconDelete /></n-icon>
            </template>
          </n-button>
        </n-flex>

        <n-button dashed size="small" style="align-self: flex-start" @click="addClass">
          <template #icon>
            <n-icon><IconAdd /></n-icon>
          </template>
          Add class
        </n-button>
      </n-space>

      <n-space vertical :size="8">
        <n-space align="center" :size="8">
          <gaia-switch v-model:value="multipleChoices" size="small" />
          <span>Allow a file to match multiple classes</span>
        </n-space>
        <n-space align="center" :size="8">
          <gaia-switch v-model:value="mandatory" size="small" />
          <span>Require every file to match at least one class</span>
        </n-space>
      </n-space>
    </n-space>

    <template #footer>
      <n-space justify="end">
        <n-button @click="close">Cancel</n-button>
        <n-button type="primary" :disabled="!canSubmit" :loading="submitting" @click="submit">
          Start tagging
        </n-button>
      </n-space>
    </template>
  </n-modal>
</template>
